/**
 * Grid Sort Utilities
 *
 * Handles mapping of display fields to backend sortable fields.
 * This is crucial because the DataGrid displays enriched/formatted values,
 * but the backend needs to sort on the raw field values.
 *
 * Key scenarios:
 * 1. Reference fields: Display shows enriched label (e.g., "Netherlands"),
 *    but backend sorts on code (e.g., "NL")
 * 2. User fields: Display shows full name, backend sorts on user ID
 * 3. Computed fields: Display shows computed value (e.g., fullName),
 *    backend sorts on specific field (e.g., sortableLastName)
 * 4. Array fields: Display shows comma-separated labels,
 *    backend sorts on array of codes
 */

import { FIELD_USER } from '../constants/fields';

/**
 * Special field mappings where display field name differs from sort field
 * These are hardcoded exceptions that don't follow standard patterns
 */
const SPECIAL_SORT_FIELD_MAPPING = {
  // Employee fullName is computed, but we sort by lastName
  fullName: 'sortableLastName',
};

/**
 * Determine the backend sort field for a given display field
 *
 * @param {string} displayField - The field name shown in the grid
 * @param {Array} columnDefinitions - Column definitions from utilities (e.g., employeeListColumns)
 * @returns {string} The backend field name to use for sorting
 */
export const getSortFieldForDisplay = (displayField, columnDefinitions) => {
  // Check special mappings first
  if (SPECIAL_SORT_FIELD_MAPPING[displayField]) {
    return SPECIAL_SORT_FIELD_MAPPING[displayField];
  }

  // If no column definitions provided, return field as-is
  if (!columnDefinitions) {
    return displayField;
  }

  // Find the column definition
  const columnDef = columnDefinitions.find(col => col.value === displayField);
  if (!columnDef) {
    // If no definition found, assume field name is correct
    return displayField;
  }

  // For reference fields (including array fields):
  // Display shows enriched label, but backend sorts on code value
  // Example: station displays "Amsterdam Centraal" but sorts on "AMS"
  if (columnDef.reference) {
    // The field already points to the code field, so use it as-is
    // e.g., "station" field contains the code, enrichment is separate
    return displayField;
  }

  // For user fields:
  // Display shows full name, but backend sorts on user ID field
  // Example: _createdBy displays "John Doe" but sorts on _createdBy (user ID)
  if (columnDef.field === FIELD_USER) {
    // The field value is the user ID field, which is what we want to sort on
    return displayField;
  }

  // For fields with custom getCellValue:
  // These compute display values, but we need to check if there's a special sort field
  if (columnDef.getCellValue) {
    // If there's a sortField property, use it
    if (columnDef.sortField) {
      return columnDef.sortField;
    }
    // Otherwise check special mappings (already done above)
    // Default to the field value itself
    return displayField;
  }

  // For all other fields (simple fields, dates, numbers, etc.):
  // The display field is the same as the sort field
  return displayField;
};

/**
 * Convert MUI DataGrid sort model to backend sort parameters
 * This considers the column definitions to map display fields to sortable backend fields
 *
 * @param {Array} sortModel - MUI DataGrid sort model [{ field: 'fieldName', sort: 'asc'|'desc' }]
 * @param {Array} columnDefinitions - Column definitions from utilities
 * @returns {Object} Backend sort parameters { sortField, sortDirection }
 */
export const convertSortModelToBackend = (sortModel, columnDefinitions) => {
  if (!sortModel || sortModel.length === 0) {
    return {
      sortField: undefined,
      sortDirection: undefined,
    };
  }

  // MUI DataGrid supports multiple sort fields, but we'll use the first one
  const firstSort = sortModel[0];
  const displayField = firstSort.field;
  const sort = firstSort.sort;

  // Get the actual backend field to sort on
  const sortField = columnDefinitions
    ? getSortFieldForDisplay(displayField, columnDefinitions)
    : displayField;

  // Convert to uppercase for backend (Java backend expects 'ASC'/'DESC')
  const sortDirection = sort ? sort.toUpperCase() : undefined; // 'ASC' or 'DESC'

  return {
    sortField,
    sortDirection,
  };
};

/**
 * Convert backend sort parameters to MUI DataGrid sort model
 * This reverses the mapping from backend fields to display fields
 *
 * @param {string} sortField - Backend sort field name
 * @param {string} sortDirection - Backend sort direction ('asc'|'desc')
 * @param {Array} columnDefinitions - Column definitions from utilities
 * @returns {Array} MUI DataGrid sort model
 */
export const convertBackendToSortModel = (sortField, sortDirection, columnDefinitions) => {
  if (!sortField) {
    return [];
  }

  // Reverse map: backend field -> display field
  let displayField = sortField;

  // Check special mappings (reverse)
  const specialReverseMapping = Object.entries(SPECIAL_SORT_FIELD_MAPPING).reduce(
    (acc, [display, backend]) => {
      acc[backend] = display;
      return acc;
    },
    {}
  );

  if (specialReverseMapping[sortField]) {
    displayField = specialReverseMapping[sortField];
  } else if (columnDefinitions) {
    // Find column that maps to this backend field
    const column = columnDefinitions.find(
      col => getSortFieldForDisplay(col.value, columnDefinitions) === sortField
    );
    if (column) {
      displayField = column.value;
    }
  }

  // Convert from backend format (uppercase 'ASC'/'DESC') to MUI format (lowercase 'asc'/'desc')
  const normalizedDirection = sortDirection ? sortDirection.toLowerCase() : '';
  const sort = normalizedDirection === 'asc' || normalizedDirection === 'desc' ? normalizedDirection : 'asc';

  return [{ field: displayField, sort }];
};

/**
 * Check if a field is sortable based on column definition
 *
 * @param {string} fieldName - Field name to check
 * @param {Array} columnDefinitions - Column definitions from utilities
 * @returns {boolean} True if field is sortable
 */
export const isFieldSortable = (fieldName, columnDefinitions) => {
  if (!columnDefinitions) return true; // Default to sortable if no definitions

  const columnDef = columnDefinitions.find(col => col.value === fieldName);
  if (!columnDef) return true; // Default to sortable if not found

  return columnDef.sortable !== false; // Sortable by default unless explicitly set to false
};

/**
 * Get sort configuration for a column
 * This can be used when preparing columns to set sortable property
 *
 * @param {Object} columnDef - Column definition
 * @param {Array} allColumnDefinitions - All column definitions (for context)
 * @returns {Object} Sort configuration { sortable, sortField }
 */
export const getSortConfig = (columnDef, allColumnDefinitions) => {
  const sortable = columnDef.sortable !== false;
  const sortField = getSortFieldForDisplay(columnDef.value, allColumnDefinitions);

  return {
    sortable,
    sortField,
    // Include this in column metadata if needed by MUI DataGrid
    sortComparator: undefined, // We use server-side sorting, not client-side
  };
};
